import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Briefcase, Building2, FileCheck, Target, X, Loader2, Wrench, Users, ClipboardList, Calendar, SlidersHorizontal, UserCircle } from 'lucide-react';
import { cn } from '../lib/utils';
import axios from 'axios';
import AdvancedSearchModal from './AdvancedSearchModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GlobalSearch({ className, isMobile = false }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcut (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
      if (event.key === 'Escape') {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const performSearch = useCallback(async (searchQuery) => {
    if (searchQuery.length < 2) {
      setResults(null);
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/search?q=${encodeURIComponent(searchQuery)}`);
      setResults(response.data);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setIsOpen(true);

    // Debounce search
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      performSearch(value);
    }, 300);
  };

  const handleResultClick = (type, id, extra) => {
    setIsOpen(false);
    setQuery('');
    setResults(null);
    
    switch (type) {
      case 'job':
        navigate(`/jobs/${id}`);
        break;
      case 'contact':
        navigate(`/companies?highlight=${id}`);
        break;
      case 'person':
        navigate(`/people?highlight=${id}`);
        break;
      case 'proposal':
        navigate(`/proposals?highlight=${id}`);
        break;
      case 'opportunity':
        navigate(`/opportunities?highlight=${id}`);
        break;
      case 'equipment':
        navigate(`/maintenance?highlight=${id}`);
        break;
      case 'team':
        navigate(`/team?highlight=${id}`);
        break;
      case 'field_report':
        // Navigate based on report type
        if (extra?.report_type === 'precon') {
          navigate(`/precon/${id}`);
        } else if (extra?.report_type === 'soft_dig') {
          navigate(`/soft-dig/${id}`);
        } else if (extra?.report_type === 'locate') {
          navigate(`/locate/${id}`);
        } else {
          navigate(`/field-reports`);
        }
        break;
      case 'imported_event':
        // Navigate to calendar with the event date selected
        if (extra?.start_date) {
          navigate(`/calendar?date=${extra.start_date}&showLegacy=true&highlight=${id}`);
        } else {
          navigate(`/calendar?showLegacy=true`);
        }
        break;
      default:
        break;
    }
  };

  const clearSearch = () => {
    setQuery('');
    setResults(null);
    setIsOpen(false);
  };

  const getIcon = (type) => {
    switch (type) {
      case 'job': return <Briefcase className="w-4 h-4 text-amber-500" />;
      case 'contact': return <Building2 className="w-4 h-4 text-blue-500" />;
      case 'proposal': return <FileCheck className="w-4 h-4 text-green-500" />;
      case 'opportunity': return <Target className="w-4 h-4 text-purple-500" />;
      case 'equipment': return <Wrench className="w-4 h-4 text-orange-500" />;
      case 'team': return <Users className="w-4 h-4 text-cyan-500" />;
      case 'field_report': return <ClipboardList className="w-4 h-4 text-rose-500" />;
      case 'imported_event': return <Calendar className="w-4 h-4 text-gray-500" />;
      default: return null;
    }
  };

  const hasResults = results && (
    results.jobs?.length > 0 || 
    results.contacts?.length > 0 || 
    results.people?.length > 0 ||
    results.proposals?.length > 0 ||
    results.opportunities?.length > 0 ||
    results.equipment?.length > 0 ||
    results.team?.length > 0 ||
    results.field_reports?.length > 0 ||
    results.imported_events?.length > 0
  );

  return (
    <>
    <div ref={containerRef} className={cn("relative", className)} data-testid="global-search">
      {/* Search Input */}
      <div className={cn(
        "relative flex items-center gap-2 bg-slate-800 rounded-md transition-colors",
        isMobile ? "px-3 py-2" : "px-3 py-1.5",
        isOpen && "ring-1 ring-amber-500/50"
      )}>
        <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          placeholder={isMobile ? "Search..." : "Search (⌘K)"}
          className="bg-transparent border-none outline-none text-sm text-white placeholder-slate-400 w-full"
          data-testid="global-search-input"
        />
        {isLoading && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
        {query && !isLoading && (
          <button 
            onClick={clearSearch} 
            className="p-0.5 hover:bg-slate-700 rounded"
            data-testid="global-search-clear"
          >
            <X className="w-3 h-3 text-slate-400" />
          </button>
        )}
        {/* Advanced Search Button */}
        <button
          onClick={() => { setIsOpen(false); setShowAdvanced(true); }}
          className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-amber-400 transition-colors"
          title="Advanced Search"
          data-testid="advanced-search-trigger"
        >
          <SlidersHorizontal className="w-4 h-4" />
        </button>
      </div>

      {/* Results Dropdown */}
      {isOpen && query.length >= 2 && (
        <div 
          className={cn(
            "absolute z-[100] bg-slate-900 border border-slate-700 rounded-lg shadow-xl overflow-hidden",
            isMobile 
              ? "left-0 right-0 top-full mt-2 max-h-[60vh]" 
              : "left-0 right-0 top-full mt-2 min-w-[300px] max-h-[400px]"
          )}
          data-testid="global-search-results"
        >
          {!hasResults && !isLoading && (
            <div className="px-4 py-6 text-center text-slate-400 text-sm">
              No results found for "{query}"
            </div>
          )}

          {hasResults && (
            <div className="overflow-y-auto max-h-[380px]">
              {/* Jobs */}
              {results.jobs?.length > 0 && (
                <div className="border-b border-slate-700 last:border-b-0">
                  <div className="px-3 py-1.5 bg-slate-800/50 text-xs font-semibold text-slate-400 uppercase tracking-wide">
                    Jobs ({results.jobs.length})
                  </div>
                  {results.jobs.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleResultClick('job', item.id)}
                      className="w-full px-3 py-2 flex items-center gap-3 hover:bg-slate-800 transition-colors text-left"
                      data-testid={`search-result-job-${item.id}`}
                    >
                      {getIcon('job')}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white truncate">
                          {item.job_number}
                        </div>
                        <div className="text-xs text-slate-400 truncate">
                          {item.job_title || item.client_name}
                        </div>
                      </div>
                      <span className={cn(
                        "text-xs px-2 py-0.5 rounded-full",
                        item.status === 'Completed' ? "bg-green-500/20 text-green-400" :
                        item.status === 'In Progress' ? "bg-blue-500/20 text-blue-400" :
                        "bg-slate-500/20 text-slate-400"
                      )}>
                        {item.status}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Contacts */}
              {results.contacts?.length > 0 && (
                <div className="border-b border-slate-700 last:border-b-0">
                  <div className="px-3 py-1.5 bg-slate-800/50 text-xs font-semibold text-slate-400 uppercase tracking-wide">
                    Companies ({results.contacts.length})
                  </div>
                  {results.contacts.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleResultClick('contact', item.id)}
                      className="w-full px-3 py-2 flex items-center gap-3 hover:bg-slate-800 transition-colors text-left"
                      data-testid={`search-result-contact-${item.id}`}
                    >
                      {getIcon('contact')}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white truncate">
                          {item.company_name}
                        </div>
                        <div className="text-xs text-slate-400 truncate">
                          {item.primary_contact_name || item.email}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* People/Contacts */}
              {results.people?.length > 0 && (
                <div className="border-b border-slate-700 last:border-b-0">
                  <div className="px-3 py-1.5 bg-slate-800/50 text-xs font-semibold text-slate-400 uppercase tracking-wide">
                    Contacts ({results.people.length})
                  </div>
                  {results.people.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleResultClick('person', item.id)}
                      className="w-full px-3 py-2 flex items-center gap-3 hover:bg-slate-800 transition-colors text-left"
                      data-testid={`search-result-person-${item.id}`}
                    >
                      <UserCircle className="w-5 h-5 text-cyan-400" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white truncate">
                          {item.full_name}
                        </div>
                        <div className="text-xs text-slate-400 truncate">
                          {item.company_name || item.email || item.job_title}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Proposals */}
              {results.proposals?.length > 0 && (
                <div className="border-b border-slate-700 last:border-b-0">
                  <div className="px-3 py-1.5 bg-slate-800/50 text-xs font-semibold text-slate-400 uppercase tracking-wide">
                    Proposals ({results.proposals.length})
                  </div>
                  {results.proposals.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleResultClick('proposal', item.id)}
                      className="w-full px-3 py-2 flex items-center gap-3 hover:bg-slate-800 transition-colors text-left"
                      data-testid={`search-result-proposal-${item.id}`}
                    >
                      {getIcon('proposal')}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white truncate">
                          {item.proposal_number || item.title}
                        </div>
                        <div className="text-xs text-slate-400 truncate">
                          {item.client_name}
                        </div>
                      </div>
                      <span className={cn(
                        "text-xs px-2 py-0.5 rounded-full",
                        item.status === 'Accepted' ? "bg-green-500/20 text-green-400" :
                        item.status === 'Sent' ? "bg-blue-500/20 text-blue-400" :
                        "bg-slate-500/20 text-slate-400"
                      )}>
                        {item.status}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Opportunities */}
              {results.opportunities?.length > 0 && (
                <div className="border-b border-slate-700 last:border-b-0">
                  <div className="px-3 py-1.5 bg-slate-800/50 text-xs font-semibold text-slate-400 uppercase tracking-wide">
                    Opportunities ({results.opportunities.length})
                  </div>
                  {results.opportunities.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleResultClick('opportunity', item.id)}
                      className="w-full px-3 py-2 flex items-center gap-3 hover:bg-slate-800 transition-colors text-left"
                      data-testid={`search-result-opportunity-${item.id}`}
                    >
                      {getIcon('opportunity')}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white truncate">
                          {item.title}
                        </div>
                        <div className="text-xs text-slate-400 truncate">
                          {item.client_name}
                        </div>
                      </div>
                      <span className={cn(
                        "text-xs px-2 py-0.5 rounded-full",
                        item.status === 'won' ? "bg-green-500/20 text-green-400" :
                        item.status === 'lost' ? "bg-red-500/20 text-red-400" :
                        "bg-slate-500/20 text-slate-400"
                      )}>
                        {item.status}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Equipment/Maintenance */}
              {results.equipment?.length > 0 && (
                <div className="border-b border-slate-700 last:border-b-0">
                  <div className="px-3 py-1.5 bg-slate-800/50 text-xs font-semibold text-slate-400 uppercase tracking-wide">
                    Equipment ({results.equipment.length})
                  </div>
                  {results.equipment.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleResultClick('equipment', item.id)}
                      className="w-full px-3 py-2 flex items-center gap-3 hover:bg-slate-800 transition-colors text-left"
                      data-testid={`search-result-equipment-${item.id}`}
                    >
                      {getIcon('equipment')}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white truncate">
                          {item.name}
                        </div>
                        <div className="text-xs text-slate-400 truncate">
                          {item.model || item.serial_number}
                        </div>
                      </div>
                      {item.status && (
                        <span className={cn(
                          "text-xs px-2 py-0.5 rounded-full",
                          item.status === 'Active' ? "bg-green-500/20 text-green-400" :
                          item.status === 'In Service' ? "bg-blue-500/20 text-blue-400" :
                          "bg-slate-500/20 text-slate-400"
                        )}>
                          {item.status}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* Team Members */}
              {results.team?.length > 0 && (
                <div className="border-b border-slate-700 last:border-b-0">
                  <div className="px-3 py-1.5 bg-slate-800/50 text-xs font-semibold text-slate-400 uppercase tracking-wide">
                    Team ({results.team.length})
                  </div>
                  {results.team.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleResultClick('team', item.id)}
                      className="w-full px-3 py-2 flex items-center gap-3 hover:bg-slate-800 transition-colors text-left"
                      data-testid={`search-result-team-${item.id}`}
                    >
                      {getIcon('team')}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white truncate">
                          {item.full_name}
                        </div>
                        <div className="text-xs text-slate-400 truncate">
                          {item.title || item.email}
                        </div>
                      </div>
                      <span className={cn(
                        "text-xs px-2 py-0.5 rounded-full capitalize",
                        item.role === 'admin' ? "bg-amber-500/20 text-amber-400" :
                        item.role === 'super_user' ? "bg-purple-500/20 text-purple-400" :
                        "bg-slate-500/20 text-slate-400"
                      )}>
                        {item.role?.replace('_', ' ')}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Field Reports */}
              {results.field_reports?.length > 0 && (
                <div className="border-b border-slate-700 last:border-b-0">
                  <div className="px-3 py-1.5 bg-slate-800/50 text-xs font-semibold text-slate-400 uppercase tracking-wide">
                    Field Reports ({results.field_reports.length})
                  </div>
                  {results.field_reports.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleResultClick('field_report', item.id, { report_type: item.report_type })}
                      className="w-full px-3 py-2 flex items-center gap-3 hover:bg-slate-800 transition-colors text-left"
                      data-testid={`search-result-field-report-${item.id}`}
                    >
                      {getIcon('field_report')}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white truncate">
                          {item.title}
                        </div>
                        <div className="text-xs text-slate-400 truncate">
                          {item.job_number} • {item.location}
                        </div>
                      </div>
                      <span className={cn(
                        "text-xs px-2 py-0.5 rounded-full capitalize",
                        item.report_type === 'precon' ? "bg-blue-500/20 text-blue-400" :
                        item.report_type === 'soft_dig' ? "bg-orange-500/20 text-orange-400" :
                        item.report_type === 'locate' ? "bg-green-500/20 text-green-400" :
                        "bg-slate-500/20 text-slate-400"
                      )}>
                        {item.report_type?.replace('_', ' ')}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Legacy/Imported Events */}
              {results.imported_events?.length > 0 && (
                <div className="border-b border-slate-700 last:border-b-0">
                  <div className="px-3 py-1.5 bg-slate-800/50 text-xs font-semibold text-slate-400 uppercase tracking-wide">
                    Legacy Jobs ({results.imported_events.length})
                  </div>
                  {results.imported_events.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleResultClick('imported_event', item.id, { start_date: item.start_date })}
                      className="w-full px-3 py-2 flex items-center gap-3 hover:bg-slate-800 transition-colors text-left"
                      data-testid={`search-result-imported-${item.id}`}
                    >
                      {getIcon('imported_event')}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white truncate">
                          {item.title}
                        </div>
                        <div className="text-xs text-slate-400 truncate">
                          {item.start_date} • {item.location || 'No location'}
                        </div>
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-500/20 text-gray-400">
                        Legacy
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
    
    {/* Advanced Search Modal */}
    <AdvancedSearchModal 
      open={showAdvanced} 
      onOpenChange={setShowAdvanced} 
    />
    </>
  );
}
